import { QueryClient, hashKey, type QueryKey, type QueryFunction } from "@tanstack/react-query";
import { cashPinRequired, requestCashPin } from "./cash-pin-gate";
import { authenticatedCacheScope, setAuthenticatedCacheScope } from "./auth-cache-scope";
import { clearOfflineDocumentCache } from "./offline";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    if (res.status === 401) {
      throw new Error("Your session has expired. Please refresh the page and log in again to continue.");
    }
    throw new Error(`${res.status}: ${text}`);
  }
}

// Helper to safely convert Date objects to ISO strings before JSON serialization
// This avoids issues with Date.prototype.toISOString being corrupted or Date-like objects
function safeSerialize(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  // Handle Date objects (including date-fns returned dates)
  if (obj instanceof Date) {
    if (isNaN(obj.getTime())) {
      return null;
    }
    // Build ISO string manually to avoid prototype issues
    const year = obj.getUTCFullYear();
    const month = String(obj.getUTCMonth() + 1).padStart(2, '0');
    const day = String(obj.getUTCDate()).padStart(2, '0');
    const hours = String(obj.getUTCHours()).padStart(2, '0');
    const minutes = String(obj.getUTCMinutes()).padStart(2, '0');
    const seconds = String(obj.getUTCSeconds()).padStart(2, '0');
    const ms = String(obj.getUTCMilliseconds()).padStart(3, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}Z`;
  }
  
  // Handle Date-like objects that have getTime but aren't instanceof Date
  if (typeof obj === 'object' && obj !== null && 'getTime' in obj && typeof (obj as any).getTime === 'function') {
    try {
      const time = (obj as any).getTime();
      if (typeof time === 'number' && !isNaN(time)) {
        const d = new Date(time);
        const year = d.getUTCFullYear();
        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        const hours = String(d.getUTCHours()).padStart(2, '0');
        const minutes = String(d.getUTCMinutes()).padStart(2, '0');
        const seconds = String(d.getUTCSeconds()).padStart(2, '0');
        const ms = String(d.getUTCMilliseconds()).padStart(3, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}Z`;
      }
    } catch {
      // Fall through to regular object handling
    }
  }
  
  if (Array.isArray(obj)) {
    return obj.map(safeSerialize);
  }
  
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = safeSerialize(value);
    }
    return result;
  }
  return obj;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  if(typeof navigator!=="undefined"&&!navigator.onLine&&!["GET","HEAD","OPTIONS"].includes(method.toUpperCase())&&/^\/api\/maintenance(?:\/|$)/.test(url)){
    throw new Error("No connection — reconnect and submit again. لا يوجد اتصال — أعد الاتصال ثم أرسل مجددًا.");
  }
  // Safely serialize data to handle Date objects before JSON.stringify
  const safeData = data ? safeSerialize(data) : undefined;

  // Auto-attach the current workspace id (mirrors `fetchAPI` in lib/api.ts) so
  // mutations guarded by the server's `requireWorkspaceAccess` middleware don't
  // fail with "Workspace ID is required". Purely additive: only sent when present
  // and never overrides a URL that already carries a `workspaceId=` query param.
  const wsId = (() => {
    try { return localStorage.getItem("currentWorkspaceId") || ""; } catch { return ""; }
  })();
  const headers: Record<string, string> = {};
  if (safeData) headers["Content-Type"] = "application/json";
  if (wsId && !url.includes("workspaceId=")) headers["x-workspace-id"] = wsId;

  const serializedBody = safeData ? JSON.stringify(safeData) : undefined;
  const originalRequest = {
    method,
    headers,
    body: serializedBody,
    credentials: "include" as const,
  };
  let res = await fetch(url, originalRequest);
  if (!res.ok && !url.includes("/api/cash/on-shift") && !url.includes("/api/cash/profile-pin")) {
    const payload = await res.clone().json().catch(() => null);
    const branch = cashPinRequired(payload);
    if (branch !== null) {
      const branchId = branch || (safeData as { branchId?: string } | undefined)?.branchId;
      if (!branchId) throw new Error("Responsible manager confirmation needs a branch. Please refresh and retry.");
      const retryRequest = payload?.details?.retryRequest as { method?: string; path?: string; body?: unknown } | undefined;
      const retryUrl = retryRequest?.path || url;
      const retryMethod = retryRequest?.method || method;
      const path = new URL(retryUrl, window.location.origin);
      if (path.origin !== window.location.origin || !path.pathname.startsWith("/api/") || !["POST", "PUT", "PATCH", "DELETE"].includes(retryMethod.toUpperCase())) {
        throw new Error("Invalid cash confirmation request. Nothing was retried.");
      }
      const retryBody = retryRequest ? (retryRequest.body === undefined ? undefined : JSON.stringify(safeSerialize(retryRequest.body))) : serializedBody;
      await requestCashPin({ branchId, workspaceId: wsId, intent: { method: retryMethod, path: path.pathname + path.search, ...(retryBody !== undefined ? { body: JSON.parse(retryBody) } : {}) } });
      const nowWorkspace = (() => { try { return localStorage.getItem("currentWorkspaceId") || ""; } catch { return ""; } })();
      if (nowWorkspace !== wsId) throw new Error("Workspace changed. Nothing was retried.");
      // Exactly one replay with the original bytes, headers and idempotency key.
      res = await fetch(retryUrl, { ...originalRequest, method: retryMethod, headers: { ...headers, ...(retryBody !== undefined ? { "Content-Type": "application/json" } : {}) }, body: retryBody });
    }
  }

  await throwIfResNotOk(res);
  if (method !== "GET" && /\/api\/cash\/counts(?:\/|$|\?)/.test(url)) {
    void queryClient.invalidateQueries({ queryKey: ["branch-cash", "on-shift"] });
  }
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey, signal }) => {
    const url = queryKey.join("/") as string;

    // Auto-attach the current workspace id (mirrors `apiRequest` / `fetchAPI`)
    // so workspace-scoped GETs guarded by `requireWorkspaceAccess` resolve even
    // when localStorage wasn't threaded through the query key. Only sent when
    // present and never when the URL already carries a `workspaceId=` param.
    const wsId = (() => {
      try { return localStorage.getItem("currentWorkspaceId") || ""; } catch { return ""; }
    })();
    const headers: Record<string, string> = {};
    if (wsId && !url.includes("workspaceId=")) headers["x-workspace-id"] = wsId;

    const res = await fetch(url, {
      headers,
      credentials: "include",
      signal,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

// The original query keys stay intact for prefix invalidation and the default
// fetcher. Hashing by the authenticated actor additionally isolates *every*
// user-dependent query, including workspace-only and endpoint-only keys.
export const authenticatedQueryHash = (key: QueryKey) => hashKey([authenticatedCacheScope(), key]);
let cacheTransition = 0;

export async function switchAuthenticatedQueryScope(
  client: QueryClient,
  userId: string | null,
  workspaceId: string | null,
  options: {force?:boolean; preserveOfflineCache?:boolean} = {},
) {
  const next = JSON.stringify([userId, workspaceId]);
  const transition = ++cacheTransition;
  if (!options.force && next === authenticatedCacheScope()) return false;
  await client.cancelQueries();
  if (transition !== cacheTransition) return false;
  client.clear();
  if (!options.preserveOfflineCache) {
    try { await clearOfflineDocumentCache(); }
    catch (error) { console.error("Could not clear offline snapshots; identity-scoped reads remain isolated:", error); }
  }
  if (transition !== cacheTransition) return false;
  setAuthenticatedCacheScope(userId, workspaceId);
  return true;
}

export async function refreshAccessQueries(client: QueryClient) {
  const filter = {
    predicate: (query: {queryKey: readonly unknown[]}) => typeof query.queryKey[0] === "string" &&
      (/^\/api\/people-access(?:\/|$)/.test(query.queryKey[0]) ||
        ["/api/me", "/api/auth/me", "/api/roles", "/api/users"].includes(query.queryKey[0])),
  };
  await client.cancelQueries(filter);
  await client.invalidateQueries(filter);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      queryKeyHashFn: authenticatedQueryHash,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
